import { ActivatedRouteSnapshot, Routes } from '@angular/router';
import { HomeComponent } from 'src/app/features/home/home.component';
import { CallbackComponent } from 'src/app/features/callback/callback.component';
import { MainLayoutComponent } from './features/main-layout/main-layout.component';
import { OnboardingLayoutComponent } from './features/onboarding/onboarding-layout/onboarding-layout.component';
import { CustomAuthGuard } from './core/guards/custom-auth.guard';
import { publicOnlyGuard } from './core/guards/public-only.guard';
import { inject } from '@angular/core';
import { OnboardingGuard } from './core/guards/onboarding.guard';
import { PageNotFoundComponent } from 'src/app/features/page-error/page-not-found-404/page-not-found.component';
import { PageError500Component } from 'src/app/features/page-error/page-error-500/page-error-500.component';
import { PageError422Component } from 'src/app/features/page-error/page-error-422/page-error-422.component';
import { PageGlobalErrorComponent } from 'src/app/features/page-error/page-error-generic/error-page.component';
import { VerifyEmailComponent } from 'src/app/features/page-error/verify-email/verify-email.component';
import { ForbiddenComponent } from 'src/app/features/page-error/page-error-forbidden-403/page-error-forbidden.component';
import { PushNotificationComponent } from 'src/app/shared/components/push-notification/push-notification.component';
import { FeatureFlagGuard } from 'src/app/core/guards/feature-flag.guard';
import { UserService } from 'src/app/core/services/user.service';
import { map } from 'rxjs/operators';
import { of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { isNativeApp } from './core/auth/native-auth';
import { patientsResolver } from './core/resolvers/patients.resolver';

/**
 * Feature flag canMatch guard factory
 * Feature: 013-shell-navigation
 * 
 * Creates a canMatch guard that checks if a feature is enabled for the user.
 * Used for conditional route component selection.
 * 
 * IMPORTANT: This guard waits for the user profile to load before checking
 * the feature flag. This ensures the feature flag is evaluated correctly
 * even on initial page load or deep links.
 * 
 * @param featureFlag - The feature flag key to check
 * @returns CanMatchFn that returns Observable<boolean>
 */
function featureMatchGuard(featureFlag: string) {
  return () => {
    // LOCAL PREVIEW ONLY (Feature 019): the gitignored environment.ts may set
    // `devOverrideMessagesFlag: true` to force the Messages section's route gate
    // open without an Auth0 flag. Scoped to the `messages` flag only, and false
    // in every committed environment (template/prod), so real builds are
    // unaffected and no other feature is influenced.
    if (
      featureFlag === 'messages' &&
      (environment as { devOverrideMessagesFlag?: boolean }).devOverrideMessagesFlag === true
    ) {
      return of(true);
    }
    // LOCAL PREVIEW ONLY (Feature 020-pulse-tracker): same escape hatch as
    // Messages above, scoped to the `pulse` flag. Lets Pedro exercise the mock
    // Pulse look-and-feel on Android before the Auth0 `pulse` flag exists. False
    // in every committed environment (template/prod), so real builds are gated
    // by the real Auth0 feature flag exactly like every other feature.
    if (
      featureFlag === 'pulse' &&
      (environment as { devOverridePulseFlag?: boolean }).devOverridePulseFlag === true
    ) {
      return of(true);
    }
    const userService = inject(UserService);
    // Wait for profile to load, then check feature flag
    return userService.getProfile(false).pipe(
      map(user => {
        if (!user?.app_metadata?.features) {
          return false;
        }
        return user.app_metadata.features[featureFlag as keyof typeof user.app_metadata.features] === true;
      })
    );
  };
}

export const routes: Routes = [
  // Feature 017 — public landing page at '/'.
  // On native (Capacitor) we load a dead-simple sign-in screen instead of the
  // marketing landing: installed-app users just want to log in, and it gives a
  // clean splash -> spinner -> home transition. Web is unchanged.
  {
    path: '',
    loadComponent: () => isNativeApp()
      ? import('./features/native-landing/native-landing.component').then(m => m.NativeLandingComponent)
      : import('./features/public-landing/public-landing-page.component').then(m => m.PublicLandingPageComponent),
    canActivate: [publicOnlyGuard],
    pathMatch: 'full'
  },
  // Explicitly define public routes with NO authentication
  {
    path: 'login',
    loadComponent: () => import('./features/public-landing/legacy-login-redirect.component').then(m => m.LegacyLoginRedirectComponent),
    pathMatch: 'full'
  },
  {
    path: 'search',
    loadComponent: () => import('./features/public-search/public-search.component').then(m => m.PublicSearchComponent),
    pathMatch: 'full'
  },
  {
    path: 'check-id',
    loadComponent: () => import('./features/check-id/check-id.component').then(m => m.CheckIdComponent),
    pathMatch: 'full'
  },
  // Public account/data deletion page (Google Play "Account deletion" policy).
  // Intentionally unauthenticated so the Play reviewer and any user can open
  // https://rettx.eu/account-deletion directly without signing in.
  {
    path: 'account-deletion',
    loadComponent: () => import('./features/account-deletion/account-deletion.component').then(m => m.AccountDeletionComponent),
    pathMatch: 'full'
  },
  {
    path: 'callback',
    component: CallbackComponent,
    pathMatch: 'full'
  },
  {
    path: 'register',
    redirectTo: 'login',
    pathMatch: 'full'
  },
  // Error pages with shell navigation using ErrorLayoutComponent
  // Each error page is a separate route to avoid path conflicts with MainLayoutComponent
  {
    path: 'page-error-500',
    loadComponent: () => import('./features/error-layout/error-layout.component').then(m => m.ErrorLayoutComponent),
    children: [{ path: '', component: PageError500Component }]
  },
  {
    path: 'page-error-forbidden-403',
    loadComponent: () => import('./features/error-layout/error-layout.component').then(m => m.ErrorLayoutComponent),
    children: [{ path: '', component: ForbiddenComponent }]
  },
  {
    path: 'page-error-422',
    loadComponent: () => import('./features/error-layout/error-layout.component').then(m => m.ErrorLayoutComponent),
    children: [{ path: '', component: PageError422Component }]
  },
  {
    path: 'page-not-found-404',
    loadComponent: () => import('./features/error-layout/error-layout.component').then(m => m.ErrorLayoutComponent),
    children: [{ path: '', component: PageNotFoundComponent }]
  },
  // Public error pages (no authentication, no shell - for critical errors)
  {
    path: 'verify-email',
    component: VerifyEmailComponent
  },
  {
    path: 'global-error',
    component: PageGlobalErrorComponent
  },
  // Onboarding routes with simplified layout (header only, NO navigation)
  // Feature: 013-shell-navigation - Onboarding uses separate layout without sidebar/bottom-nav
  {
    path: 'onboarding',
    component: OnboardingLayoutComponent,
    canActivate: [CustomAuthGuard, OnboardingGuard],
    children: [
      { 
        path: 'language', 
        loadComponent: () => import('src/app/features/onboarding/onboarding-language/onboarding-language.component').then(m => m.OnboardingLanguageComponent)
      },
      { 
        path: 'profile', 
        loadComponent: () => import('src/app/features/onboarding/onboarding-profile/onboarding-profile.component').then(m => m.OnboardingProfileComponent)
      },
      { 
        path: 'privacy-policy', 
        loadComponent: () => import('src/app/features/onboarding/onboarding-privacy/onboarding-privacy.component').then(m => m.OnboardingPrivacyPolicyComponent)
      },
      { path: '', redirectTo: 'language', pathMatch: 'full' }
    ]
  },
  // Protected routes - authentication required (main app with full navigation)
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [CustomAuthGuard], // Custom guard redirects to /login instead of Auth0
    children: [
      // Main app group (only accessible when onboarding is complete)
      {
        path: '',
        canActivate: [OnboardingGuard],
        children: [
          { path: '', redirectTo: 'patients', pathMatch: 'full' },
          {
            path: 'patients',
            component: HomeComponent,
            resolve: {
              // FR-011/FR-012 (Auth0 refresh-token migration): gate the authed
              // load on a restored+authenticated session and recover from a
              // token/resolver failure instead of hanging on the unicorn screen.
              // The successful-resolve path is unchanged (emits the same
              // IPatientResponseDTO[]). See patients.resolver.ts.
              patients: () => patientsResolver()
            }
          },
          // Profile route (Feature: 013-shell-navigation)
          {
            path: 'profile',
            loadComponent: () => import('src/app/features/profile-page/profile-page.component').then(m => m.ProfilePageComponent)
          },
          // Surveys route with canMatch guard pattern (Feature: 013-shell-navigation)
          // When surveys feature is enabled, load SurveysPageComponent (patient-scoped MVP)
          {
            path: 'surveys',
            loadComponent: () => import('src/app/features/surveys-page/surveys-page.component').then(m => m.SurveysPageComponent),
            canMatch: [featureMatchGuard('surveys')]
          },
          // When surveys feature is disabled, load placeholder
          {
            path: 'surveys',
            loadComponent: () => import('src/app/shared/components/feature-placeholder/feature-placeholder.component').then(m => m.FeaturePlaceholderComponent),
            data: { featureName: 'surveys', placeholderConfig: 'surveys' }
          },
          // Care Profile routes with canMatch guard pattern (Feature: 015-care-profile)
          // Route-based architecture: status, edit, create as separate components for SCSS budget
          {
            path: 'care-profile',
            loadChildren: () => import('src/app/features/care-profile/care-profile.routes').then(m => m.CARE_PROFILE_ROUTES),
            canMatch: [featureMatchGuard('care_profile')]
          },
          // When care-profile feature is disabled, load placeholder
          {
            path: 'care-profile',
            loadComponent: () => import('src/app/shared/components/feature-placeholder/feature-placeholder.component').then(m => m.FeaturePlaceholderComponent),
            data: { featureName: 'care-profile', placeholderConfig: 'care-profile' }
          },
          // Notifications route with canMatch guard pattern (Feature: 013-shell-navigation)
          // When notifications feature is enabled, load NotificationSettingsComponent
          {
            path: 'notifications',
            loadComponent: () => import('src/app/features/notification-settings/notification-settings.component').then(m => m.NotificationSettingsComponent),
            canMatch: [featureMatchGuard('push_notification')]
          },
          // When notifications feature is disabled, load placeholder
          {
            path: 'notifications',
            loadComponent: () => import('src/app/shared/components/feature-placeholder/feature-placeholder.component').then(m => m.FeaturePlaceholderComponent),
            data: { featureName: 'notifications', placeholderConfig: 'notifications' }
          },
          // Messages section with canMatch guard pattern (Feature: 019-message-center)
          // When messages feature is enabled, load the caregiver Messages section.
          {
            path: 'messages',
            loadChildren: () => import('src/app/features/messages/messages.routes').then(m => m.MESSAGES_ROUTES),
            canMatch: [featureMatchGuard('messages')]
          },
          // When messages feature is disabled, load placeholder
          {
            path: 'messages',
            loadComponent: () => import('src/app/shared/components/feature-placeholder/feature-placeholder.component').then(m => m.FeaturePlaceholderComponent),
            data: { featureName: 'messages', placeholderConfig: 'messages' }
          },
          // Pulse section with canMatch guard pattern (Feature: 020-pulse-tracker)
          // When pulse feature is enabled, load the caregiver Pulse tracker (Phase 1
          // runs entirely on mocked data behind the swappable PulseDataSource token).
          {
            path: 'pulse',
            loadChildren: () => import('src/app/features/pulse/pulse.routes').then(m => m.PULSE_ROUTES),
            canMatch: [featureMatchGuard('pulse')]
          },
          // When pulse feature is disabled, load placeholder
          {
            path: 'pulse',
            loadComponent: () => import('src/app/shared/components/feature-placeholder/feature-placeholder.component').then(m => m.FeaturePlaceholderComponent),
            data: { featureName: 'pulse', placeholderConfig: 'pulse' }
          },
          {
            path: 'admin/notification-management/push-tokens/advanced-config-8f2a9c4d',
            component: PushNotificationComponent,
            canActivate: [FeatureFlagGuard],
            data: { requiredFeature: 'admin_notification' }
          }
        ]
      }
    ]
  },
  // Wildcard route - unknown paths go to 404 page
  {
    path: '**',
    redirectTo: 'page-not-found-404',
    pathMatch: 'full'
  }
];